import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Phone, MapPin, Star, Calendar, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Price from '../../components/common/Price';
import { useCRM } from '../../context/CRMContext';

const SOURCE_COLORS = {
    'Walk-in':      { bg: '#EEF2FF', color: '#4338CA' },
    'Website':      { bg: '#EFF6FF', color: '#1D4ED8' },
    'Referral':     { bg: '#ECFDF5', color: '#047857' },
    'Broker':       { bg: '#FFFBEB', color: '#B45309' },
    'Social Media': { bg: '#FDF4FF', color: '#7E22CE' },
    'IVR / Call':   { bg: '#FEF2F2', color: '#B91C1C' },
};

const CONFIG_BG = {
    '2 BHK':     '#EFF6FF',
    '3 BHK':     '#F5F3FF',
    'Penthouse': '#FFFBEB',
};

const initials = (name = '') =>
    name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

const avatarHue = (name = '') => {
    const hues = [220, 250, 280, 160, 30, 0, 200];
    return hues[(name.charCodeAt(0) || 0) % hues.length];
};

const relativeTime = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
};

const CRMCard = ({ lead, index }) => {
    const navigate = useNavigate();
    const { movingLeadIds } = useCRM();

    const isMoving = Boolean(movingLeadIds && movingLeadIds[String(lead.id)]);
    const srcStyle = SOURCE_COLORS[lead.source] || { bg: '#F3F4F6', color: '#374151' };
    const cfgBg = CONFIG_BG[lead.configuration] || '#F3F4F6';
    const hue = avatarHue(lead.client);
    const isHighPriority = lead.priority === 'high';

    // Navigate to full detail page — NOT side panel
    const handleClick = () => {
        if (isMoving) return; // Prevent navigation while saving move
        navigate('/crm/detail', { state: { lead } });
    };

    return (
        <Draggable draggableId={String(lead.id)} index={index}>
            {(provided, snapshot) => {
                // Only consider actively dragging when mouse is held down and not animating drop
                const isDragging = snapshot.isDragging && !snapshot.isDropAnimating;

                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={handleClick}
                        className="bg-white dark:bg-[#0d1117] text-slate-800 dark:text-[#f0f6fc] transition-all relative"
                        style={{
                            ...provided.draggableProps.style,
                            border: isDragging
                                ? '1px solid #D9531E'
                                : isMoving
                                ? '1px solid #38bdf8'
                                : '1px solid var(--border-card, #30363d)',
                            borderRadius: 8,
                            padding: '10px 11px',
                            cursor: isDragging ? 'grabbing' : isMoving ? 'wait' : 'grab',
                            boxShadow: isDragging
                                ? '0 12px 28px rgba(217,83,30,0.22), 0 4px 10px rgba(0,0,0,0.06)'
                                : isMoving
                                ? '0 0 0 1px rgba(56,189,248,0.2), 0 2px 6px rgba(0,0,0,0.08)'
                                : '0 1px 2px rgba(0,0,0,0.04)',
                            opacity: isMoving ? 0.88 : 1,
                            userSelect: 'none',
                            transform: isDragging && provided.draggableProps.style?.transform
                                ? `${provided.draggableProps.style.transform} rotate(1.2deg)`
                                : provided.draggableProps.style?.transform,
                            transition: isDragging
                                ? 'none'
                                : provided.draggableProps.style?.transition,
                        }}
                    >
                        {/* Row 1: Avatar + Name + Sync Loader or Star */}
                        <div className="flex items-center gap-2 mb-2">
                            <div
                                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 crm-card-avatar"
                                style={{ background: `hsl(${hue},60%,90%)`, color: `hsl(${hue},60%,32%)` }}
                            >
                                {initials(lead.client)}
                            </div>
                            <span className="text-xs font-semibold text-gray-800 dark:text-[#f0f6fc] flex-1 truncate hover:text-[#D9531E] dark:hover:text-[#D9531E] transition-colors">
                                {lead.client || 'Unknown'}
                            </span>
                            {isMoving ? (
                                <span className="flex items-center gap-1 text-[10px] font-semibold text-sky-500 dark:text-sky-400 shrink-0" title="Saving changes...">
                                    <Loader2 size={11} className="animate-spin" />
                                    <span className="text-[9px] uppercase tracking-wider">Saving</span>
                                </span>
                            ) : (
                                <Star
                                    size={11}
                                    fill={isHighPriority ? '#F59E0B' : 'none'}
                                    stroke={isHighPriority ? '#F59E0B' : '#D1D5DB'}
                                    className="shrink-0"
                                />
                            )}
                        </div>

                    {/* Row 2: Unit + Config badge */}
                    {(lead.unit || lead.configuration) && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                            {lead.unit && (
                                <span className="text-[10px] text-gray-500 dark:text-[#848d97] flex items-center gap-0.5">
                                    <MapPin size={9} className="text-gray-400 dark:text-[#6e7681]" />{lead.unit}
                                </span>
                            )}
                            {lead.configuration && (
                                <span
                                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-[#848d97] border border-transparent dark:border-[#30363d]"
                                >
                                    {lead.configuration}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Row 3: Phone */}
                    {lead.phone && (
                        <div className="flex items-center gap-1 mb-1.5">
                            <Phone size={9} className="text-gray-400 dark:text-[#6e7681] shrink-0" />
                            <span className="text-[10px] text-gray-500 dark:text-[#848d97] truncate">{lead.phone}</span>
                        </div>
                    )}

                    {/* Row 4: Budget + Source + Date */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-[#30363d] gap-2 mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                            {lead.budget > 0 && (
                                <span className="text-[11px] font-bold text-gray-700 dark:text-[#f0f6fc] truncate">
                                    ₹ <Price amount={Number(lead.budget)} />
                                </span>
                            )}
                            {lead.source && (
                                <span
                                    className="crm-source-badge text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 border border-transparent dark:border-[#30363d]"
                                    style={{ background: srcStyle.bg, color: srcStyle.color }}
                                >
                                    {lead.source}
                                </span>
                            )}
                        </div>
                        {lead.createdAt && (
                            <span className="text-[9.5px] text-gray-400 dark:text-[#6e7681] shrink-0 flex items-center gap-0.5">
                                <Calendar size={8} />{relativeTime(lead.createdAt)}
                            </span>
                        )}
                    </div>
                </div>
            );
        }}
    </Draggable>
    );
};

export default CRMCard;